import axios from "axios";
import React, { Component } from "react";
import { Alert, Button, Col, Input, Label, Row, Spinner } from "reactstrap";
import cookie from "react-cookies";
import PlacesAutocomplete from "./common/PlacesAutocomplete";
import util from "../util";
import constants from "../Constants";
import { API_BASE } from "../pages/api-docs/partnerApiContent";
import {
  QIKTRUCK_PARTNER_API_TERMS_URL,
  QIKTRUCK_PRIVACY_URL,
} from "../constants/legal";
import { JOB_TITLES, selectOptionsWithCurrent } from "../constants/contactOptions";
import "./PartnerAccessRequestForm.css";

const SA_PROVINCES = [
  "Eastern Cape",
  "Free State",
  "Gauteng",
  "KwaZulu-Natal",
  "Limpopo",
  "Mpumalanga",
  "Northern Cape",
  "North West",
  "Western Cape",
];

function componentText(c) {
  if (!c) return "";
  return c.longText || c.long_name || c.shortText || c.short_name || "";
}

function parseAddressComponents(components) {
  const find = (...types) => {
    for (const t of types) {
      const c = (components || []).find((x) => {
        const ts = x.types || x.type || [];
        return ts.includes(t);
      });
      if (c) return componentText(c);
    }
    return "";
  };
  return {
    city: find("locality", "administrative_area_level_2", "sublocality"),
    province: find("administrative_area_level_1"),
    postal_code: find("postal_code"),
    country: find("country"),
  };
}

function matchSaProvince(name) {
  if (!name) return "";
  const normalized = name.toLowerCase().replace(/^province of\s+/i, "").trim();
  const hit = SA_PROVINCES.find(
    (p) => p.toLowerCase() === normalized || p.toLowerCase().includes(normalized)
  );
  return hit || name;
}

const EMPTY = {
  email: "",
  apitype: "enterprise",
  description: "",
  contact_name: "",
  contact_job_title: "",
  contact_phone: "",
  technical_contact_email: "",
  company_legal_name: "",
  company_trading_name: "",
  company_registration_number: "",
  vat_number: "",
  company_phone: "",
  company_registered_address: "",
  company_city: "",
  company_province: "Gauteng",
  company_postal_code: "",
  company_country: "South Africa",
  website_url: "",
  domains: "",
  social_linkedin: "",
  social_facebook: "",
  social_instagram: "",
  social_other: "",
  integration_category: "erp",
  expected_monthly_volume: "",
  planned_go_live_date: "",
  terms_partner_api: false,
  terms_not_competitor: false,
  terms_authorized_signatory: false,
};

const FIELD_SECTION = {
  email: "contact",
  contact_name: "contact",
  contact_job_title: "contact",
  contact_phone: "contact",
  company_legal_name: "company",
  company_registration_number: "company",
  company_registered_address: "company",
  company_city: "company",
  company_province: "company",
  company_postal_code: "company",
  website_url: "company",
  domains: "domains",
  description: "integration",
  terms_partner_api: "terms",
  terms_not_competitor: "terms",
  terms_authorized_signatory: "terms",
};

function apiRegistrationsUrl() {
  return `${(constants.baseURL || API_BASE).replace(/\/$/, "")}/api-registrations`;
}

function collectValidationErrors(state) {
  const errors = [];
  const add = (field, message) => errors.push({ field, section: FIELD_SECTION[field], message });

  if (!state.email || state.email.length < 5) add("email", "Enter a valid work email.");
  if (!state.contact_name?.trim()) add("contact_name", "Contact full name is required.");
  if (!state.contact_job_title?.trim()) add("contact_job_title", "Contact job title is required.");
  if (!state.contact_phone?.trim()) add("contact_phone", "Contact phone is required.");
  if (!state.company_legal_name?.trim()) add("company_legal_name", "Company legal name is required.");
  if (!state.company_registration_number?.trim()) {
    add("company_registration_number", "Company registration number is required.");
  }
  if (!state.company_registered_address?.trim()) {
    add("company_registered_address", "Company registered address is required.");
  }
  if (!state.company_city?.trim()) add("company_city", "Company city is required.");
  if (!state.company_province?.trim()) add("company_province", "Company province is required.");
  if (!state.company_postal_code?.trim()) add("company_postal_code", "Company postal code is required.");
  if (!state.website_url?.trim()) add("website_url", "Company website is required.");
  if (!String(state.domains || "").trim()) add("domains", "List at least one domain.");
  if (!state.description?.trim() || state.description.trim().length < 10) {
    add("description", "Describe your integration (min 10 characters).");
  }
  if (!state.terms_partner_api) {
    add("terms_partner_api", "Accept the Partner API terms and privacy policy.");
  }
  if (!state.terms_not_competitor) {
    add("terms_not_competitor", "Confirm your organisation is not a competing logistics platform.");
  }
  if (!state.terms_authorized_signatory) {
    add("terms_authorized_signatory", "Confirm you are authorised to bind your company.");
  }
  return errors;
}

class PartnerAccessRequestForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      ...EMPTY,
      loading: false,
      done: false,
      error: "",
      validationErrors: [],
      fieldErrors: {},
      openSections: {
        contact: true,
        company: false,
        domains: false,
        integration: false,
        terms: false,
      },
      submitterCustomerId: null,
    };
    this.feedbackRef = React.createRef();
  }

  toggleSection = (id) => {
    this.setState((prev) => ({
      openSections: { ...prev.openSections, [id]: !prev.openSections[id] },
    }));
  };

  sectionErrorCount = (sectionId) => {
    const { fieldErrors } = this.state;
    return Object.keys(fieldErrors).filter((f) => FIELD_SECTION[f] === sectionId).length;
  };

  scrollToFeedback = () => {
    try {
      this.feedbackRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    } catch {
      /* noop */
    }
  };

  clearFieldError = (field) => {
    if (!this.state.fieldErrors[field]) return;
    const fieldErrors = { ...this.state.fieldErrors };
    delete fieldErrors[field];
    const validationErrors = Object.values(fieldErrors);
    this.setState({
      fieldErrors,
      validationErrors,
      error: validationErrors[0] || "",
    });
  };

  setField = (key, value) => {
    this.clearFieldError(key);
    this.setState({ [key]: value, error: "" });
  };

  section = (id, title, children) => {
    const open = this.state.openSections[id];
    const errCount = this.sectionErrorCount(id);
    return (
      <div
        className={
          "form-section" +
          (open ? " is-open" : " is-collapsed") +
          (errCount ? " has-errors" : "")
        }
      >
        <button
          type="button"
          className="form-section-header"
          aria-expanded={open}
          onClick={() => this.toggleSection(id)}
        >
          <span className="form-section-title">
            {title}
            {errCount > 0 ? (
              <span className="form-section-error-badge" title={`${errCount} issue(s) in this section`}>
                {errCount}
              </span>
            ) : null}
          </span>
          <i className={"mdi form-section-chevron " + (open ? "mdi-chevron-up" : "mdi-chevron-down")} aria-hidden />
        </button>
        {open ? <div className="form-section-body">{children}</div> : null}
      </div>
    );
  };

  componentDidMount() {
    const { defaultEmail, knownDomains } = this.props;
    const patch = {};
    const sessionId = cookie.load("session-id-qt");
    // Only use defaultEmail when not logged in - logged-in users get account email from profile prefill.
    if (defaultEmail && !sessionId) patch.email = defaultEmail;
    if (knownDomains?.length && !this.state.domains) {
      patch.domains = knownDomains.map((d) => d.domain || d).join("\n");
    }
    if (Object.keys(patch).length) this.setState(patch);
    this.prefillFromAccountProfile(defaultEmail, knownDomains);
  }

  prefillFromAccountProfile = (defaultEmail, knownDomains) => {
    const sessionId = cookie.load("session-id-qt");
    if (!sessionId) return;

    axios
      .get((constants.baseURL || API_BASE) + "/merchant/account-profile", {
        headers: { Authorization: sessionId },
      })
      .then((res) => {
        if (res.status !== 200 || !res.data) return;
        const { account, business } = res.data;
        const patch = {};

        if (account?.customerid) {
          patch.submitterCustomerId = account.customerid;
          try {
            cookie.save("localsessionid", account.customerid, { path: "/" });
          } catch {
            /* noop */
          }
        }
        if (account?.email) patch.email = account.email;
        else if (!this.state.email) patch.email = defaultEmail || "";
        const fullName = [account?.firstname, account?.lastname].filter(Boolean).join(" ").trim();
        if (!this.state.contact_name && fullName) patch.contact_name = fullName;
        if (!this.state.contact_phone && (account?.mobilenumber || business?.company_phone)) {
          patch.contact_phone = account?.mobilenumber || business?.company_phone;
        }
        if (!this.state.contact_job_title && business?.contact_job_title) {
          patch.contact_job_title = business.contact_job_title;
        }
        if (!this.state.technical_contact_email && business?.finance_email) {
          patch.technical_contact_email = business.finance_email;
        }
        if (!this.state.company_legal_name && business?.company_legal_name) {
          patch.company_legal_name = business.company_legal_name;
        }
        if (!this.state.company_trading_name && business?.company_trading_name) {
          patch.company_trading_name = business.company_trading_name;
        }
        if (!this.state.company_registration_number && business?.company_registration_number) {
          patch.company_registration_number = business.company_registration_number;
        }
        if (!this.state.vat_number && business?.vat_number) patch.vat_number = business.vat_number;
        if (!this.state.company_phone && business?.company_phone) patch.company_phone = business.company_phone;
        if (!this.state.company_registered_address && business?.company_registered_address) {
          patch.company_registered_address = business.company_registered_address;
        }
        if (!this.state.company_city && business?.company_city) patch.company_city = business.company_city;
        if (!this.state.company_province && business?.company_province) {
          patch.company_province = business.company_province;
        }
        if (!this.state.company_postal_code && business?.company_postal_code) {
          patch.company_postal_code = business.company_postal_code;
        }
        if (!this.state.company_country && business?.company_country) {
          patch.company_country = business.company_country;
        }
        if (!this.state.website_url && business?.website_url) patch.website_url = business.website_url;
        if (!this.state.integration_category && business?.integration_category) {
          patch.integration_category = business.integration_category;
        }
        if (!this.state.expected_monthly_volume && business?.expected_monthly_volume) {
          patch.expected_monthly_volume = business.expected_monthly_volume;
        }

        const domainLines = [];
        if (Array.isArray(business?.domains)) domainLines.push(...business.domains);
        if (knownDomains?.length) {
          knownDomains.forEach((d) => domainLines.push(d.domain || d));
        }
        if (!this.state.domains && domainLines.length) {
          patch.domains = [...new Set(domainLines.map((d) => String(d).trim()).filter(Boolean))].join("\n");
        }

        if (Object.keys(patch).length) this.setState(patch);
      })
      .catch(() => {});
  };

  validate = () => collectValidationErrors(this.state);

  applyValidationErrors = (errors) => {
    const openSections = { ...this.state.openSections };
    const fieldErrors = {};
    errors.forEach((e) => {
      openSections[e.section] = true;
      fieldErrors[e.field] = e.message;
    });
    this.setState({
      validationErrors: errors.map((e) => e.message),
      fieldErrors,
      error: errors[0]?.message || "",
      openSections,
    });
    this.scrollToFeedback();
  };

  submit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    const errors = this.validate();
    if (errors.length) {
      this.applyValidationErrors(errors);
      return;
    }
    const s = this.state;
    const { sourceRef, knownDomains, submitterCustomerId: submitterProp } = this.props;
    const submitterCustomerId =
      this.state.submitterCustomerId ||
      submitterProp ||
      cookie.load("localsessionid") ||
      null;
    const sessionId = cookie.load("session-id-qt");
    this.setState({
      loading: true,
      error: "",
      validationErrors: [],
      fieldErrors: {},
    });

    const application = {
      contact_name: s.contact_name,
      contact_job_title: s.contact_job_title,
      contact_phone: s.contact_phone,
      technical_contact_email: s.technical_contact_email || s.email.trim(),
      company_legal_name: s.company_legal_name,
      company_trading_name: s.company_trading_name,
      company_registration_number: s.company_registration_number,
      vat_number: s.vat_number,
      company_phone: s.company_phone,
      company_registered_address: s.company_registered_address,
      company_city: s.company_city,
      company_province: s.company_province,
      company_postal_code: s.company_postal_code,
      company_country: s.company_country,
      website_url: s.website_url,
      domains: s.domains,
      social_linkedin: s.social_linkedin,
      social_facebook: s.social_facebook,
      social_instagram: s.social_instagram,
      social_other: s.social_other,
      integration_category: s.integration_category,
      expected_monthly_volume: s.expected_monthly_volume,
      planned_go_live_date: s.planned_go_live_date,
      terms_partner_api: s.terms_partner_api,
      terms_not_competitor: s.terms_not_competitor,
      terms_authorized_signatory: s.terms_authorized_signatory,
      known_widget_domains: (knownDomains || []).map((d) => d.domain || d),
      submitter_customerid: submitterCustomerId,
    };

    const headers = sessionId ? { Authorization: sessionId } : {};

    axios
      .post(
        apiRegistrationsUrl(),
        {
          email: s.email.trim(),
          description: s.description.trim(),
          apitype: s.apitype,
          application,
          source_:
            util.navigator_() +
            util.source_() +
            " qtref:" +
            (sourceRef || "customer-hq:partner-access"),
        },
        { headers }
      )
      .then(() => {
        if (this.props.onSuccess) this.props.onSuccess(s.email.trim());
        this.setState({
          loading: false,
          done: true,
          error: "",
          validationErrors: [],
          fieldErrors: {},
        });
      })
      .catch((e) => {
        const raw = e.response?.data;
        let msg = "Could not submit your request. Email support@qiktruck.co.za.";
        if (typeof raw === "string" && raw.trim()) msg = raw;
        else if (raw?.message) msg = raw.message;
        else if (Array.isArray(raw?.errors)) msg = raw.errors.join(" ");
        this.setState({
          loading: false,
          done: false,
          error: msg,
          validationErrors: [msg],
          fieldErrors: {},
        });
        this.scrollToFeedback();
      });
  };

  handleCompanyAddressPlace = (place) => {
    const address = place?.formatted_address || "";
    const parsed = parseAddressComponents(place?.raw?.addressComponents);
    const patch = { company_registered_address: address };
    if (parsed.city) patch.company_city = parsed.city;
    if (parsed.province) patch.company_province = matchSaProvince(parsed.province);
    if (parsed.postal_code) patch.company_postal_code = parsed.postal_code;
    if (parsed.country) patch.company_country = parsed.country;
    this.setState(patch);
    this.clearFieldError("company_registered_address");
    ["company_city", "company_province", "company_postal_code"].forEach((f) => this.clearFieldError(f));
  };

  fieldError = (key) => this.state.fieldErrors[key];

  field = (key, label, opts = {}) => {
    const { type = "text", placeholder = "", md = 6, rows, options } = opts;
    const err = this.fieldError(key);
    const value = this.state[key];
    return (
      <Col md={md} xs={12} className="mb-2">
        <Label className="small fw-semibold" for={`partner-field-${key}`}>
          {label}
        </Label>
        {type === "select" && options ? (
          <Input
            id={`partner-field-${key}`}
            type="select"
            value={value}
            invalid={!!err}
            onChange={(e) => this.setField(key, e.target.value)}
          >
            <option value="">Select…</option>
            {selectOptionsWithCurrent(options, value).map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </Input>
        ) : (
          <Input
            id={`partner-field-${key}`}
            type={type}
            rows={rows}
            value={value}
            placeholder={placeholder}
            invalid={!!err}
            onChange={(e) =>
              this.setField(key, type === "checkbox" ? e.target.checked : e.target.value)
            }
          />
        )}
        {err ? <div className="invalid-feedback d-block">{err}</div> : null}
      </Col>
    );
  };

  renderErrorSummary() {
    const { validationErrors, error, loading } = this.state;
    const messages = validationErrors.length ? validationErrors : error ? [error] : [];
    if (!messages.length) return null;
    return (
      <Alert color="danger" className="qt-partner-form-errors mb-0">
        <div className="fw-semibold mb-1">
          <i className="mdi mdi-alert-circle-outline me-1" />
          {loading ? "Submission failed" : `Please fix ${messages.length === 1 ? "this issue" : `${messages.length} issues`}`}
        </div>
        {messages.length === 1 ? (
          <div>{messages[0]}</div>
        ) : (
          <ul className="mb-0 ps-3">
            {messages.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        )}
      </Alert>
    );
  }

  renderSubmitBar() {
    const { loading } = this.state;
    return (
      <div className="qt-partner-form-footer" ref={this.feedbackRef}>
        {this.renderErrorSummary()}
        <Button
          color="primary"
          className="btn-submit"
          type="submit"
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Submitting…
            </>
          ) : (
            <>
              <i className="mdi mdi-send-outline me-1" />
              Submit API access request
            </>
          )}
        </Button>
      </div>
    );
  }

  render() {
    const { knownDomains, compact, requireLogin } = this.props;
    const s = this.state;
    const sessionId = cookie.load("session-id-qt");

    if (requireLogin && !sessionId) {
      return (
        <Alert color="info" className="mb-0">
          <strong>Sign in required.</strong> Partner API applications must be submitted from your QikTruck
          business account.{" "}
          <a href="/login?redirect=/api-docs">Sign in</a> or apply from{" "}
          <a href="/integrations?type=partner">Integrations → Partner API</a>.
        </Alert>
      );
    }

    if (s.done) {
      return (
        <Alert color="success" className="mb-0">
          <strong>Request received.</strong> We&apos;ll review your company details and follow up at{" "}
          <strong>{s.email}</strong>.
        </Alert>
      );
    }

    return (
      <form className={"qt-partner-access-form" + (compact ? " qt-partner-access-form--compact" : "")} onSubmit={this.submit} noValidate>

        {this.section("contact", "Contact", (
          <Row>
            {this.field("email", "Contact / integration email *", {
              type: "email",
              placeholder: "dev@yourcompany.co.za",
              md: 12,
            })}
            {this.field("contact_name", "Full name *", { placeholder: "Jane Dlamini" })}
            {this.field("contact_job_title", "Job title *", { type: "select", options: JOB_TITLES })}
            {this.field("contact_phone", "Phone / mobile *", { placeholder: "+27 82 123 4567" })}
            {this.field("technical_contact_email", "Technical contact email", {
              type: "email",
              placeholder: "Same as work email if blank",
            })}
            <Col md={6} xs={12} className="mb-2">
              <Label className="small fw-semibold">Plan</Label>
              <Input
                type="select"
                value={s.apitype}
                onChange={(e) => this.setState({ apitype: e.target.value })}
              >
                <option value="enterprise">Production integration</option>
                <option value="free">Sandbox / evaluation</option>
              </Input>
            </Col>
          </Row>
        ))}

        {this.section("company", "Company", (
          <Row>
            {this.field("company_legal_name", "Legal company name *", { md: 6 })}
            {this.field("company_trading_name", "Trading name (if different)", { md: 6 })}
            {this.field("company_registration_number", "Registration no. (CK / IT) *", {
              placeholder: "e.g. 2019/123456/07",
            })}
            {this.field("vat_number", "VAT number (optional)", { placeholder: "4123456789" })}
            {this.field("company_phone", "Company switchboard (optional)", { placeholder: "+27 11 123 4567" })}
            <Col md={12} xs={12} className="mb-2">
              <Label className="small fw-semibold" for="partner-company-address">
                Registered company address *
              </Label>
              <PlacesAutocomplete
                type="text"
                className={"form-control" + (this.fieldError("company_registered_address") ? " is-invalid" : "")}
                id="partner-company-address"
                placeholder="Search registered address in South Africa…"
                defaultValue={s.company_registered_address}
                onInputChange={(val) => this.setField("company_registered_address", val)}
                onPlaceSelected={this.handleCompanyAddressPlace}
              />
              {this.fieldError("company_registered_address") ? (
                <div className="invalid-feedback d-block">
                  {this.fieldError("company_registered_address")}
                </div>
              ) : (
                <div className="text-muted small mt-1">
                  Start typing to search - city, province, and postal code fill in automatically when you pick a result.
                </div>
              )}
            </Col>
            {this.field("company_city", "City *", { placeholder: "Johannesburg" })}
            <Col md={6} xs={12} className="mb-2">
              <Label className="small fw-semibold" for="partner-field-company_province">
                Province *
              </Label>
              <Input
                id="partner-field-company_province"
                type="select"
                invalid={!!this.fieldError("company_province")}
                value={s.company_province}
                onChange={(e) => this.setField("company_province", e.target.value)}
              >
                {SA_PROVINCES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </Input>
              {this.fieldError("company_province") ? (
                <div className="invalid-feedback d-block">{this.fieldError("company_province")}</div>
              ) : null}
            </Col>
            {this.field("company_postal_code", "Postal code *", { placeholder: "2196" })}
            {this.field("company_country", "Country", { md: 6 })}
            {this.field("website_url", "Company website *", {
              placeholder: "https://yourcompany.co.za",
              md: 12,
            })}
          </Row>
        ))}

        {this.section("domains", "Domains & online presence", (
          <>
          {knownDomains?.length > 0 && (
            <div className="known-domains">
              <span className="text-muted small me-1">Your verified widget domains:</span>
              {knownDomains.map((d) => (
                <span key={d.domain || d} className="known-domain-chip">
                  {d.domain || d}
                  {d.status ? ` (${d.status})` : ""}
                </span>
              ))}
            </div>
          )}
          <Row>
            <Col md={12} className="mb-2">
              <Label className="small fw-semibold" for="partner-field-domains">
                Domains for API / widget use *
              </Label>
              <Input
                id="partner-field-domains"
                type="textarea"
                rows={3}
                invalid={!!this.fieldError("domains")}
                value={s.domains}
                placeholder={"One per line, e.g.\nyourstore.co.za\nshop.yourbrand.com"}
                onChange={(e) => this.setField("domains", e.target.value)}
              />
              {this.fieldError("domains") ? (
                <div className="invalid-feedback d-block">{this.fieldError("domains")}</div>
              ) : null}
            </Col>
            {this.field("social_linkedin", "LinkedIn", { placeholder: "https://linkedin.com/company/..." })}
            {this.field("social_facebook", "Facebook", { placeholder: "https://facebook.com/..." })}
            {this.field("social_instagram", "Instagram", { placeholder: "https://instagram.com/..." })}
            {this.field("social_other", "Other social / marketplace URL", { md: 12 })}
          </Row>
          </>
        ))}

        {this.section("integration", "Integration", (
          <Row>
            <Col md={6} xs={12} className="mb-2">
              <Label className="small fw-semibold">Integration type</Label>
              <Input
                type="select"
                value={s.integration_category}
                onChange={(e) => this.setState({ integration_category: e.target.value })}
              >
                <option value="erp">ERP / back-office</option>
                <option value="tms">TMS / fleet system</option>
                <option value="ecommerce">E-commerce platform</option>
                <option value="marketplace">Marketplace</option>
                <option value="custom">Custom application</option>
              </Input>
            </Col>
            {this.field("expected_monthly_volume", "Expected monthly deliveries (optional)", {
              placeholder: "e.g. 150 trips/month",
            })}
            {this.field("planned_go_live_date", "Planned go-live date (optional)", {
              type: "date",
              md: 6,
            })}
            <Col md={12} className="mb-2">
              <Label className="small fw-semibold" for="partner-field-description">
                Describe your integration *
              </Label>
              <Input
                id="partner-field-description"
                type="textarea"
                rows={4}
                invalid={!!this.fieldError("description")}
                value={s.description}
                placeholder="What system will connect, what workflows (quote, book, track), go-live timeline…"
                onChange={(e) => this.setField("description", e.target.value)}
              />
              {this.fieldError("description") ? (
                <div className="invalid-feedback d-block">{this.fieldError("description")}</div>
              ) : null}
            </Col>
          </Row>
        ))}

        {this.section("terms", "Terms & declarations", (
          <div className={"terms-block" + (this.sectionErrorCount("terms") ? " terms-block--invalid" : "")}>
            <label className={this.fieldError("terms_partner_api") ? "is-invalid-label" : ""}>
              <input
                type="checkbox"
                checked={s.terms_partner_api}
                onChange={(e) => this.setField("terms_partner_api", e.target.checked)}
              />
              <span>
                I accept the{" "}
                <a
                  href={QIKTRUCK_PARTNER_API_TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms &amp; Conditions
                </a>{" "}
                and{" "}
                <a href={QIKTRUCK_PRIVACY_URL} target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>{" "}
                for Partner API access.
              </span>
            </label>
            <label className={this.fieldError("terms_not_competitor") ? "is-invalid-label" : ""}>
              <input
                type="checkbox"
                checked={s.terms_not_competitor}
                onChange={(e) => this.setField("terms_not_competitor", e.target.checked)}
              />
              <span>
                I confirm our organisation is <strong>not a competing logistics or truck-hire platform</strong>{" "}
                and will not resell, white-label, or rebrand QikTruck services in competition with QikTruck.
              </span>
            </label>
            <label className={this.fieldError("terms_authorized_signatory") ? "is-invalid-label" : ""}>
              <input
                type="checkbox"
                checked={s.terms_authorized_signatory}
                onChange={(e) => this.setField("terms_authorized_signatory", e.target.checked)}
              />
              <span>
                I am authorised to submit this application on behalf of the company named above and to bind it to
                these terms.
              </span>
            </label>
            {this.sectionErrorCount("terms") ? (
              <div className="text-danger small mt-2">
                {Object.keys(this.state.fieldErrors)
                  .filter((f) => FIELD_SECTION[f] === "terms")
                  .map((f) => (
                    <div key={f}>{this.state.fieldErrors[f]}</div>
                  ))}
              </div>
            ) : null}
          </div>
        ))}

        {this.renderSubmitBar()}
      </form>
    );
  }
}

export default PartnerAccessRequestForm;
